/**
 * Cost arithmetic — turns a unit of work into what it cost us.
 *
 * Pure functions over RATES in config.ts. No numbers are declared here.
 *
 * Every result carries a `basis` so a figure's reliability is never implied:
 *
 *   "rate"      a configured rate was applied.
 *   "fallback"  the model is not in the rate card, so the MOST expensive known
 *               rate was applied. Over-counting is a visible, bounded error;
 *               under-counting is how cost goes unnoticed.
 *   "unpriced"  the provider publishes no price for this call. Recorded as zero
 *               and flagged, never quietly assumed to be free.
 */

import { CREDIT_MICRO_USD, RATES } from "./config";
import type {
  CallPriceUsd,
  CloudflareRates,
  MicroUsd,
  ModelRate,
  XRates,
} from "./types";

export type CostBasis = "rate" | "fallback" | "unpriced";

export interface Cost {
  readonly microUsd: MicroUsd;
  readonly basis: CostBasis;
}

/** USD to integer micro-dollars. */
export function toMicroUsd(usd: number): MicroUsd {
  return Math.round(usd * 1_000_000);
}

/** Integer micro-dollars to USD, for display. */
export function fromMicroUsd(microUsd: MicroUsd): number {
  return microUsd / 1_000_000;
}

/**
 * What a call actually cost, in credits: always rounded UP, minimum 1.
 *
 * Rounding up rather than to nearest means a one-micro-dollar call is never
 * billed as zero credits, and a fraction of a credit is never quietly
 * absorbed — the same "over-count rather than under-count" rule `aiCompletionCost`
 * applies to micro-dollars, one level up.
 */
export function creditsFor(microUsd: MicroUsd): number {
  return Math.max(1, Math.ceil(microUsd / CREDIT_MICRO_USD));
}

// ── AI ───────────────────────────────────────────────────────────────────────

export interface TokenCounts {
  readonly promptTokens: number;
  /** Visible output tokens. */
  readonly completionTokens: number;
  /**
   * Reasoning tokens, reported separately by providers that think (Gemini's
   * `thoughtsTokenCount`). Omitting them from pricing was one half of the
   * fifteenfold under-count this module replaced.
   */
  readonly thinkingTokens?: number;
}

export interface AiCost extends Cost {
  /** The rate-card model actually used to price the call. */
  readonly pricedAs: string;
  /**
   * The volume this price was computed from, with thinking folded into
   * output where the provider bills it that way. The ledger tallies these
   * per model so a published price change can be projected over real usage.
   */
  readonly billedTokens: { promptTokens: number; outputTokens: number };
}

function nonNegative(n: number | undefined): number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * The prices in force for a model at a given moment, applying any published
 * scheduled change.
 */
export function rateAt(
  rate: ModelRate,
  at: Date
): { inputUsdPerMillion: number; outputUsdPerMillion: number } {
  const change = rate.scheduledChange;
  if (change && at.getTime() >= Date.parse(`${change.from}T00:00:00Z`)) {
    return {
      inputUsdPerMillion: change.inputUsdPerMillion,
      outputUsdPerMillion: change.outputUsdPerMillion,
    };
  }
  return {
    inputUsdPerMillion: rate.inputUsdPerMillion,
    outputUsdPerMillion: rate.outputUsdPerMillion,
  };
}

function mostExpensiveModel(): [string, ModelRate] {
  const entries = Object.entries(RATES.ai.models);
  if (entries.length === 0) {
    throw new Error("RATES.ai.models is empty; no AI call can be priced.");
  }
  // Ranked by output rate: output dominates the bill for generation work.
  return entries.reduce((a, b) =>
    b[1].outputUsdPerMillion > a[1].outputUsdPerMillion ? b : a
  );
}

/**
 * What one completed model call cost.
 *
 * Tokens multiplied by a per-million rate already yields micro-dollars, since
 * both scale by a million. The sum is rounded UP once, so a call is
 * over-counted by at most one micro-dollar and never under-counted.
 */
export function aiCompletionCost(
  model: string,
  tokens: TokenCounts,
  at: Date = new Date()
): AiCost {
  const known = RATES.ai.models[model];
  const [pricedAs, rate] = known ? [model, known] : mostExpensiveModel();
  const prices = rateAt(rate, at);

  const prompt = nonNegative(tokens.promptTokens);
  const output =
    nonNegative(tokens.completionTokens) +
    (rate.thinkingBilledAsOutput ? nonNegative(tokens.thinkingTokens) : 0);

  const raw =
    prompt * prices.inputUsdPerMillion + output * prices.outputUsdPerMillion;

  return {
    // The epsilon absorbs binary float noise (800 * 0.3 is 240.00000000000003)
    // so ceil does not add a micro-dollar that was never charged.
    microUsd: Math.max(0, Math.ceil(raw - 1e-6)),
    basis: known ? "rate" : "fallback",
    pricedAs,
    billedTokens: { promptTokens: prompt, outputTokens: output },
  };
}

// ── Images ───────────────────────────────────────────────────────────────────

/**
 * What `count` generated images cost us. An unknown model is priced at the
 * dearest known image rate, for the same reason AI calls are: an over-count
 * is visible, an under-count is how cost goes unnoticed.
 */
export function imageGenerationCost(model: string, count: number): Cost {
  const known = RATES.images.models[model];
  const rates = Object.values(RATES.images.models);
  const usd = known
    ? known.usdPerImage
    : Math.max(...rates.map((r) => r.usdPerImage));
  const n = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  return {
    microUsd: toMicroUsd(usd) * n,
    basis: known ? "rate" : "fallback",
  };
}

// ── Transcription ────────────────────────────────────────────────────────────

/**
 * What transcribing `seconds` of audio cost. A model that is not on the rate
 * card is priced at the dearest known transcription rate, for the same reason
 * the AI table over-counts rather than under-counts.
 */
export function transcriptionCost(model: string, seconds: number): Cost {
  const models = Object.entries(RATES.transcription.models);
  const known = RATES.transcription.models[model];
  const rate = known
    ? known.usdPerMinute
    : Math.max(...models.map(([, r]) => r.usdPerMinute ?? 0));
  if (rate === null) return { microUsd: 0, basis: "unpriced" };
  const minutes = Math.max(0, seconds) / 60;
  return {
    microUsd: Math.max(0, Math.ceil(toMicroUsd(minutes * rate) - 1e-6)),
    basis: known ? "rate" : "fallback",
  };
}

// ── X ────────────────────────────────────────────────────────────────────────

function priceCall(usd: CallPriceUsd): Cost {
  return usd === null
    ? { microUsd: 0, basis: "unpriced" }
    : { microUsd: toMicroUsd(usd), basis: "rate" };
}

/**
 * Common top-level domains X auto-links in bare form ("example.com").
 *
 * Reference data for detection, not a price, so it lives here rather than in
 * config. Deliberately excludes file extensions that are not TLDs, because
 * developer-audience posts constantly mention "Node.js" and "config.ts", and
 * pricing those as link posts would over-count each by $0.185.
 *
 * Known gap: a bare domain on a TLD missing from this list is priced as a
 * plain post. URLs with a scheme or "www." are always caught regardless.
 */
const LINKABLE_TLDS = [
  "com",
  "org",
  "net",
  "io",
  "co",
  "ai",
  "dev",
  "app",
  "me",
  "ly",
  "gg",
  "xyz",
  "info",
  "biz",
  "tech",
  "site",
  "online",
  "blog",
  "news",
  "so",
  "tv",
  "fm",
  "to",
  "sh",
  "us",
  "uk",
  "ca",
  "au",
  "nz",
  "ie",
  "de",
  "fr",
  "es",
  "it",
  "nl",
  "se",
  "no",
  "dk",
  "fi",
  "pl",
  "pt",
  "br",
  "mx",
  "ar",
  "in",
  "jp",
  "kr",
  "cn",
  "sg",
  "hk",
  "ng",
  "za",
  "ke",
  "gh",
  "eg",
  "ae",
  "eu",
  "edu",
  "gov",
  "page",
  "link",
  "shop",
  "store",
  "cloud",
  "studio",
  "design",
  "email",
  "live",
  "media",
  "network",
  "digital",
  "agency",
  "careers",
  "jobs",
  "md",
];

const SCHEME_OR_WWW =
  /(?:\bhttps?:\/\/\S+|\bwww\.[a-z0-9-]+(?:\.[a-z0-9-]+)+)/i;

const BARE_DOMAIN = new RegExp(
  // Not preceded by "@" (an email or handle) or a word character.
  String.raw`(?<![@\w.-])` +
    String.raw`(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+` +
    `(?:${LINKABLE_TLDS.join("|")})` +
    String.raw`(?![\w-])`,
  "i"
);

/**
 * Whether X will treat this post text as containing a URL, and so bill it at
 * the with-URL rate.
 */
export function containsUrl(text: string): boolean {
  return SCHEME_OR_WWW.test(text) || BARE_DOMAIN.test(text);
}

export interface XPostCost extends Cost {
  readonly withUrl: boolean;
}

/** What creating one post on X cost, given its final text. */
export function xPostCreateCost(
  text: string,
  rates: XRates = RATES.x
): XPostCost {
  const withUrl = containsUrl(text);
  return {
    ...priceCall(withUrl ? rates.postCreateWithUrlUsd : rates.postCreateUsd),
    withUrl,
  };
}

export type XCallAction =
  | "userRead"
  | "postRead"
  | "postDelete"
  | "mediaUpload";

/** What a non-post X API call cost. */
export function xCallCost(action: XCallAction, rates: XRates = RATES.x): Cost {
  switch (action) {
    case "userRead":
      return priceCall(rates.userReadUsd);
    case "postRead":
      return priceCall(rates.postReadUsd);
    case "postDelete":
      return priceCall(rates.postDeleteUsd);
    case "mediaUpload":
      return priceCall(rates.mediaUploadUsd);
  }
}

// ── Cloudflare Browser Rendering ────────────────────────────────────────────

/**
 * What one rendered-fetch read cost, given how long the browser ran.
 *
 * Priced on the full duration, not discounted for Cloudflare's free daily
 * allowance — the free tier changes what Cloudflare bills us, not what this
 * read cost in browser time, and the admin cost view exists precisely so a
 * rising rendered-read rate is visible before the free allowance runs out.
 */
export function renderedFetchCost(
  durationMs: number,
  rates: CloudflareRates = RATES.cloudflare
): Cost {
  if (rates.browserHourUsd === null) return { microUsd: 0, basis: "unpriced" };
  const hours = Math.max(0, durationMs) / (60 * 60 * 1000);
  const usd = hours * rates.browserHourUsd;
  return {
    microUsd: Math.max(0, Math.ceil(toMicroUsd(usd) - 1e-6)),
    basis: "rate",
  };
}
